/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import useCurrentTeacher from 'common/hooks/useCurrentTeacher'
import { ClientStudentInstance } from 'common/utils/types/account-types-client'
import { Program } from 'common/utils/types/program'

import {
  DIFFICULTIES,
  THRESHOLDS,
  PROGRAM_ICONS,
  OPERATION_ICONS,
} from 'common/constants/programs'
import Close from 'assets/close.svg'
import { PROGRAM_LENGTHS } from '../program-settings/constants'

function RestartPrograms() {
  const { currentTeacher, studentsPerClassroom, ensureStudentsAndClassroom } =
    useCurrentTeacher()
  const [query, setQuery] = useState('')
  const [students, setStudents] = useState<ClientStudentInstance[]>([])
  const [selectedStudents, setSelectedStudents] = useState<
    Record<string, boolean>
  >({})
  const classroomCodeRef = useRef('')
  const [isRestarting, setIsRestarting] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const refreshStudents = () => {
    classroomCodeRef.current =
      window.parent.location.href.split('/').pop() || ''
    if (studentsPerClassroom?.[classroomCodeRef.current]) {
      setStudents(studentsPerClassroom?.[classroomCodeRef.current])
    } else setStudents([])
  }

  useEffect(() => {
    if (!currentTeacher || !studentsPerClassroom) return
    refreshStudents()
  }, [currentTeacher, studentsPerClassroom])

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data.type === 'classroom-refreshed') {
        ensureStudentsAndClassroom(event.data.students, event.data.classroom)
      }
      if (event.data.type === 'restart-programs-confirmed') {
        if (isRestarting) return
        setIsRestarting(true)
        const studentsToReset = Object.keys(selectedStudents)
          .filter((code) => selectedStudents[code])
          .map((code) => students.find((s) => s.code === code))
          .reduce(
            (acc, student) => {
              acc[student.code] = student
              return acc
            },
            {} as Record<string, ClientStudentInstance>
          )
        currentTeacher
          .resetStudentPrograms(studentsToReset)
          .then(() => {
            refreshStudents()
            setSelectedStudents({})
            setSnackbarOpen(true)
          })
          .catch(() => undefined)
          .finally(() => {
            setIsRestarting(false)
          })
      }
    }
    window.parent.addEventListener('message', handler)
    return () => {
      window.parent.removeEventListener('message', handler)
    }
  }, [selectedStudents, students, currentTeacher])

  useEffect(() => {
    setSelectedStudents(
      students.reduce((acc, student) => {
        acc[student.code] = false
        return acc
      }, {})
    )
  }, [students])

  const filteredStudents = students.filter(
    (s) => s?.displayName?.toLowerCase().includes(query.toLowerCase())
  )

  const handleToggleAll = () => {
    const filteredCodes = new Set(filteredStudents.map((s) => s.code))
    const allSelected = filteredStudents.every((s) => selectedStudents[s.code])
    const someSelected =
      filteredStudents.some((s) => selectedStudents[s.code]) && !allSelected

    setSelectedStudents((prev) => {
      const next: Record<string, boolean> = { ...prev }
      if (someSelected || allSelected) {
        filteredCodes.forEach((code) => {
          next[code] = false
        })
      } else {
        filteredCodes.forEach((code) => {
          next[code] = true
        })
      }
      return next
    })
  }

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const renderHighlighted = (text: string) => {
    if (!query.trim()) return text
    const regex = new RegExp(`(${escapeRegExp(query)})`, 'ig')
    const lower = query.toLowerCase()
    // eslint-disable-next-line react/no-array-index-key
    return text.split(regex).map((part, idx) =>
      part.toLowerCase() === lower ? (
        <Box
          key={idx}
          component='mark'
          sx={{ backgroundColor: 'secondary.light', color: 'inherit', p: 0 }}
        >
          {part}
        </Box>
      ) : (
        <React.Fragment key={idx}>{part}</React.Fragment>
      )
    )
  }

  const handleConfirmRestart = () => {
    if (isRestarting) return
    const popupButton = window.parent.document.body.querySelector(
      '#restartProgramConfirmationPopupLink'
    ) as HTMLElement
    if (popupButton) {
      popupButton.click()
    }
  }

  return (
    <Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        sx={{ bottom: '15% !important', left: '30% !important' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity='success'>
          Programs restarted successfully.
        </Alert>
      </Snackbar>
      <Typography variant='h5'>Restart Programs</Typography>
      <Typography variant='body2'>
        Select the students whose program you want to restart. Restarting a
        program will permanently delete all of their current progress and data.
        The next time these students sign in to XtraMath, they will start the
        program again from the beginning.
      </Typography>
      <Box sx={{ mt: 3 }}>
        <TextField
          label='Search by student name'
          placeholder='Name'
          variant='outlined'
          color='secondary'
          sx={{ width: '30%' }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position='end'>
                <IconButton
                  size='small'
                  edge='end'
                  aria-label='Clear search'
                  onClick={() => setQuery('')}
                >
                  <Box component='img' src={Close} alt='Clear' height={14} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
      <Box sx={{ mt: 3, maxHeight: '500px', overflowY: 'auto' }}>
        <TableContainer>
          <Table component='div' size='small'>
            <TableHead component='div'>
              <TableRow component='div'>
                <TableCell component='div'>
                  <Checkbox
                    color='secondary'
                    checked={
                      filteredStudents.length > 0 &&
                      filteredStudents.every((s) => selectedStudents[s.code])
                    }
                    indeterminate={
                      filteredStudents.length > 0 &&
                      filteredStudents.some((s) => selectedStudents[s.code]) &&
                      !filteredStudents.every((s) => selectedStudents[s.code])
                    }
                    disabled={filteredStudents.length === 0}
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell component='div'>Name</TableCell>
                <TableCell align='center' component='div'>
                  Program
                </TableCell>
                <TableCell align='center' component='div'>
                  Difficulty
                </TableCell>
                <TableCell align='center' component='div'>
                  Threshold
                </TableCell>
                <TableCell align='center' component='div'>
                  Program Length
                </TableCell>
              </TableRow>
            </TableHead>
            {filteredStudents.length > 0 ? (
              <TableBody component='div'>
                {filteredStudents
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((student) => {
                    const program = Program.fromId(student.programId)
                    const difficulty = program.problemSetId()
                    const thresholdId = program.thresholdId()
                    const assessmentOnly = program.isAssessmentOnly()
                    const mathOperationIdMix = program.mathOperationIdMix()

                    const isSelected = !!selectedStudents[student.code]

                    return (
                      <TableRow
                        key={student.code}
                        component='label'
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: isSelected
                            ? 'secondary.200'
                            : 'transparent',
                        }}
                      >
                        <TableCell component='div'>
                          <Checkbox
                            color='secondary'
                            checked={isSelected}
                            onChange={(e) =>
                              setSelectedStudents((prev) => ({
                                ...prev,
                                [student.code]: e.target.checked,
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell component='div'>
                          {renderHighlighted(student.displayName)}
                        </TableCell>
                        <TableCell align='center' component='div'>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                            }}
                          >
                            {PROGRAM_ICONS[mathOperationIdMix].map(
                              (operation) => (
                                <Box
                                  key={operation}
                                  component='img'
                                  src={OPERATION_ICONS[operation]}
                                  alt={operation}
                                  sx={{ width: 20, height: 20 }}
                                />
                              )
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align='center' component='div'>
                          {DIFFICULTIES[difficulty]}
                        </TableCell>
                        <TableCell align='center' component='div'>
                          {THRESHOLDS[thresholdId]}
                        </TableCell>
                        <TableCell align='center' component='div'>
                          {assessmentOnly
                            ? PROGRAM_LENGTHS.fluencyAssessment
                            : PROGRAM_LENGTHS.ongoing}
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            ) : (
              <TableBody component='div'>
                <TableRow component='div'>
                  <TableCell
                    colSpan={6}
                    sx={{ textAlign: 'center', borderBottom: 'none', pt: 3 }}
                  >
                    <Typography variant='body2'>
                      {query ? (
                        'No students match your search. Try a different name.'
                      ) : (
                        <>
                          There are no students in this class.{' '}
                          <Link
                            target='_parent'
                            href={`/#/teacher/add_student/${classroomCodeRef.current}`}
                          >
                            Add students
                          </Link>
                        </>
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant='contained'
          color='secondary'
          size='large'
          disabled={
            !Object.values(selectedStudents).some(Boolean) || isRestarting
          }
          onClick={handleConfirmRestart}
        >
          {isRestarting ? (
            <>
              <CircularProgress size={16} color='inherit' sx={{ mr: 1 }} />
              Restarting...
            </>
          ) : (
            'Restart'
          )}
        </Button>
      </Box>
    </Box>
  )
}

export default RestartPrograms
